import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, TextInput } from '@opendate-app/setlist';
import TurnstileChallenge from '../../../shared/TurnstileChallenge';
import TurnstileDisclosure from '../../../shared/TurnstileDisclosure';
import { formatPrice } from '../priceFormatting';

const UNAVAILABLE_MESSAGE = "Gift cards can't be applied right now. Please try again.";
const REMOVE_FAILED_MESSAGE = "We couldn't remove that gift card. Please try again.";

export default function GiftCardEntry({
  appliedGiftCards,
  fullyCovered,
  entryClosed,
  requiredCardPaymentCents,
  currency,
  turnstileSiteKey,
  onApply,
  onRemove,
  disabled,
}) {
  const turnstileRef = useRef(null);
  const coveredRef = useRef(null);
  const headingRef = useRef(null);
  const wasFullyCovered = useRef(fullyCovered);
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [removeError, setRemoveError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [announcement, setAnnouncement] = useState(null);
  const [applying, setApplying] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);

  const formatCents = (cents) => formatPrice(cents / 100, currency);

  const turnstileToken = () => {
    if (!captchaRequired || !turnstileRef.current) return Promise.resolve('');
    return turnstileRef.current.executeAsync();
  };

  const changing = applying || removingId !== null;

  useEffect(() => {
    if (fullyCovered && !wasFullyCovered.current && coveredRef.current) coveredRef.current.focus();
    wasFullyCovered.current = fullyCovered;
  }, [fullyCovered]);

  const apply = async () => {
    if (code.trim().length === 0 || changing || disabled) return;
    setError(null);
    setRemoveError(null);
    setNotice(null);
    setAnnouncement(null);
    setApplying(true);
    try {
      const result = await onApply(code.trim(), turnstileToken);
      setCaptchaRequired((current) => current || result.captchaRequired);
      if (result.ok) {
        setCode('');
      } else {
        setError(result.error || UNAVAILABLE_MESSAGE);
      }
    } catch (e) {
      setError(UNAVAILABLE_MESSAGE);
    } finally {
      if (turnstileRef.current) turnstileRef.current.reset();
      setApplying(false);
    }
  };

  const remove = async (redemptionId) => {
    if (changing || disabled) return;
    setError(null);
    setRemoveError(null);
    setNotice(null);
    setAnnouncement(null);
    setRemovingId(redemptionId);
    const removed = appliedGiftCards.find((card) => card.id === redemptionId);
    try {
      const result = await onRemove(redemptionId);
      if (!result.ok) {
        setRemoveError(result.error || REMOVE_FAILED_MESSAGE);
      } else {
        const released = result.releasedCodeLast4s.length > 0
          ? `No longer needed for this order, so removed: ${result.releasedCodeLast4s.map((last4) => `gift card •••• ${last4}`).join(', ')}.`
          : null;
        setNotice(released);
        setAnnouncement([removed && `Removed gift card •••• ${removed.code_last4}.`, released].filter(Boolean).join(' '));
        if (headingRef.current) headingRef.current.focus();
      }
    } catch (e) {
      setRemoveError(REMOVE_FAILED_MESSAGE);
    } finally {
      setRemovingId(null);
    }
  };

  const codeEntryHidden = fullyCovered || entryClosed;

  return (
    <div className="series-pass-gift-card" data-testid="series-pass-gift-card">
      <div className="series-pass-gift-card-heading" ref={headingRef} tabIndex={-1}>Gift card</div>
      {appliedGiftCards.map((card) => (
        <div className="series-pass-gift-card-applied" key={card.id} data-testid="series-pass-gift-card-applied">
          <div>
            <strong>Gift card •••• {card.code_last4}</strong>
            <div className="series-pass-gift-card-detail">
              {formatCents(card.amount_cents)} applied · {formatCents(card.balance_after_cents)} left on card
            </div>
          </div>
          <Button
            variant="link-gray"
            size="sm"
            aria-label={`Remove gift card ending ${card.code_last4}`}
            disabled={changing || disabled}
            onClick={() => remove(card.id)}
          >
            Remove
          </Button>
        </div>
      ))}
      {requiredCardPaymentCents != null && (
        <p className="series-pass-gift-card-detail">
          {formatCents(requiredCardPaymentCents)} of this order has to be paid by card.
        </p>
      )}
      {fullyCovered && (
        <div ref={coveredRef} tabIndex={-1} data-testid="series-pass-gift-card-covered">
          <Alert color="success" size="full-width" title="Your gift card covers this order." />
        </div>
      )}
      {!codeEntryHidden && (
        <div className="series-pass-gift-card-entry">
          <div className="series-pass-gift-card-input">
            <TextInput
              size="sm"
              aria-label="Gift card code"
              placeholder="Have a gift card? Enter the code"
              autoComplete="off"
              value={code}
              disabled={disabled}
              onChange={(event) => setCode(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  apply();
                }
              }}
              data-testid="series-pass-gift-card-code"
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            disabled={code.trim().length === 0 || changing || disabled}
            onClick={apply}
            data-testid="series-pass-gift-card-apply"
          >
            {applying ? 'Checking…' : 'Apply'}
          </Button>
        </div>
      )}
      {error && <p className="series-pass-error" role="alert">{error}</p>}
      {removeError && <p className="series-pass-error" role="alert">{removeError}</p>}
      {notice && <p className="series-pass-gift-card-detail" aria-hidden="true">{notice}</p>}
      <p className="sr-only" role="status">{announcement || ''}</p>
      {captchaRequired && (
        <>
          <TurnstileChallenge ref={turnstileRef} sitekey={turnstileSiteKey} />
          <TurnstileDisclosure />
        </>
      )}
    </div>
  );
}

GiftCardEntry.propTypes = {
  appliedGiftCards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      code_last4: PropTypes.string.isRequired,
      amount_cents: PropTypes.number.isRequired,
      balance_after_cents: PropTypes.number.isRequired,
    })
  ),
  fullyCovered: PropTypes.bool,
  entryClosed: PropTypes.bool,
  requiredCardPaymentCents: PropTypes.number,
  currency: PropTypes.string,
  turnstileSiteKey: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

GiftCardEntry.defaultProps = {
  appliedGiftCards: [],
  fullyCovered: false,
  entryClosed: false,
  requiredCardPaymentCents: null,
  currency: 'USD',
  turnstileSiteKey: null,
  disabled: false,
};
